import { Button } from '@HoudiniUI/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';
import { Field, FieldDescription, FieldLabel } from '@HoudiniUI/Field';
import { TextArea } from '@HoudiniUI/TextArea';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { VERIFY_LABEL } from './complianceDerived';
import { styleSheet } from './styles';

// A one-word excuse is not a decision on the record.
const MIN_REASON_LENGTH = 12;

export const ReleaseHoldDialog = ({
	open,
	onOpenChange,
	blockers,
	onConfirm,
	isPending,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	blockers: Api.CampaignComplianceFinding[];
	onConfirm: (reason: string) => void;
	isPending: boolean;
}) => {
	const [reason, setReason] = React.useState('');
	const reasonId = React.useId();
	// The parent also closes this dialog programmatically (mutation success), which never fires
	// onOpenChange — so the reset has to track `open` itself.
	React.useEffect(() => {
		setReason('');
	}, [open]);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Release the hold without fixing</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styleSheet.dialogStack]}>
					<p className={css(styleSheet.warnBox)}>
						<strong>Prefer checking first.</strong> If the client has corrected these pages,{' '}
						{VERIFY_LABEL.toLowerCase()} instead — a review that passes clears the hold with no override on the record.
					</p>
					<p className={css(styleSheet.dialogText)}>
						Releasing records your decision and <strong>submits the campaign immediately</strong>. The findings stay
						outstanding, so the provider may decline it anyway.
					</p>
					{blockers.length ? (
						<div>
							<p className={css(styleSheet.dialogText)}>
								<strong>Releasing past these blockers</strong>
							</p>
							<ul className={css(styleSheet.blockerList)}>
								{blockers.map(blocker => (
									<li key={blocker.code}>
										{blocker.title ?? blocker.code}{' '}
										<span className={css(styleSheet.blockerListCode)}>{blocker.code}</span>
									</li>
								))}
							</ul>
						</div>
					) : null}
					<Field>
						<FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
						<FieldDescription>
							Recorded permanently against this account, and visible to anyone who views it.
						</FieldDescription>
						<TextArea
							id={reasonId}
							value={reason}
							onChange={e => setReason(e.target.value)}
							placeholder='Why is it right to submit without these being fixed?'
							rows={4}
						/>
					</Field>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						variant='destructive'
						disabled={reason.trim().length < MIN_REASON_LENGTH}
						isLoading={isPending}
						onClick={() => onConfirm(reason.trim())}
					>
						Release and submit
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
